import type {
  CableRoutingResult,
  CableSpecResult,
  HeaterType,
  TwoRoomEntryWall,
  TwoRoomSaunaPosition,
} from '../../engines/types';

interface CableSpecInput {
  heaterType: HeaterType;
  saunaLighting?: "wall" | "none";
  recommendedKw: number;
  widthM: number;
  lengthM: number;
  entryWall?: TwoRoomEntryWall;
  saunaPosition?: TwoRoomSaunaPosition;
  hasRelaxRoom?: boolean;
}

/**
 * Sauna heaters connect 400V 3-phase (SS 436 40 00); size fuse and
 * conductor area from the current per phase, not from kW buckets.
 */
function getAmpsPerPhase(recommendedKw: number): number {
  return (recommendedKw * 1000) / (400 * Math.sqrt(3));
}

function getFuseLabelKey(
  recommendedKw: number,
): 'domain_cable_fuse_3x10A' | 'domain_cable_fuse_3x16A' | 'domain_cable_fuse_3x20A' | 'domain_cable_fuse_3x25A' {
  const amps = getAmpsPerPhase(recommendedKw);
  if (amps <= 10) return 'domain_cable_fuse_3x10A';
  if (amps <= 16) return 'domain_cable_fuse_3x16A';
  if (amps <= 20) return 'domain_cable_fuse_3x20A';
  return 'domain_cable_fuse_3x25A';
}

/** Returns the stable Swedish fuse label used for backward-compat display fallback. */
function getFuseLabel(recommendedKw: number): string {
  const amps = getAmpsPerPhase(recommendedKw);
  if (amps <= 10) return '3×10A trefas';
  if (amps <= 16) return '3×16A trefas';
  if (amps <= 20) return '3×20A trefas';
  return '3×25A trefas';
}

/** The feed cable for a heater of this power, e.g. "5×2.5 mm² EKLK". */
export function getCableType(recommendedKw: number): string {
  const amps = getAmpsPerPhase(recommendedKw);
  if (amps <= 10) return '5×1.5 mm² EKLK';
  if (amps <= 16) return '5×2.5 mm² EKLK';
  if (amps <= 20) return '5×4 mm² EKLK';
  return '5×6 mm² EKLK';
}

/**
 * The SiHF run carries the heater feed through the hot zone to the
 * terminals — three phases + neutral + PE, same current as the feed cable.
 * A flat 3×1.5 mm² (fine for lighting/control) cannot carry three phases
 * plus a protective conductor at heater current, so its core count and
 * area must scale with the same amps-per-phase tier as getCableType.
 */
function getSihfCableType(recommendedKw: number): string {
  const amps = getAmpsPerPhase(recommendedKw);
  if (amps <= 10) return 'SiHF 5×1.5 mm²';
  if (amps <= 16) return 'SiHF 5×2.5 mm²';
  if (amps <= 20) return 'SiHF 5×4 mm²';
  return 'SiHF 5×6 mm²';
}

function calculateCableRouting({
  entryWall = 'short',
  saunaPosition = 'left',
  hasRelaxRoom = true,
  widthM,
  lengthM,
  sihfLengthM,
}: Pick<CableSpecInput, 'entryWall' | 'saunaPosition' | 'hasRelaxRoom' | 'widthM' | 'lengthM'> & {
  sihfLengthM: number;
}): CableRoutingResult {
  const relaxZoneCableLengthM = hasRelaxRoom ? 2 : 0;

  if (entryWall === 'long') {
    const routeLabel = saunaPosition === 'left' ? 'long-wall-left-sauna' : 'long-wall-right-sauna';
    const feedCableLengthM = Number((lengthM + (saunaPosition === 'right' ? widthM : 0) + 1.5).toFixed(1));
    const serviceWall = saunaPosition === 'left' ? 'right' : 'left';

    return {
      entryWall,
      saunaPosition,
      hasRelaxRoom,
      routeLabel,
      feedCableLengthM,
      sihfLengthM,
      relaxZoneCableLengthM,
      doorLightWall: serviceWall,
      outletWall: serviceWall,
    };
  }

  const feedCableLengthM = Number((Math.max(widthM, lengthM) + 1.5).toFixed(1));
  return {
    entryWall,
    saunaPosition,
    hasRelaxRoom,
    routeLabel: 'short-wall-legacy',
    feedCableLengthM,
    sihfLengthM,
    relaxZoneCableLengthM: 0,
    doorLightWall: 'front',
    outletWall: 'front',
  };
}

export function calculateCableSpec({
  heaterType,
  saunaLighting,
  recommendedKw,
  widthM,
  lengthM,
  entryWall = 'short',
  saunaPosition = 'left',
  hasRelaxRoom = true,
}: CableSpecInput): CableSpecResult {
  const sihfLengthM = Number(Math.max(1.5, Math.min(4, widthM / 2 + 1)).toFixed(1));
  const routing = calculateCableRouting({
    entryWall,
    saunaPosition,
    hasRelaxRoom,
    widthM,
    lengthM,
    sihfLengthM,
  });
  const isLegacyRouting = routing.routeLabel === 'short-wall-legacy';
  const totalCableLengthM = Number((
    routing.feedCableLengthM
    + sihfLengthM
    + (isLegacyRouting ? 0 : routing.relaxZoneCableLengthM)
  ).toFixed(1));

  if (heaterType === 'wood') {
    const lightingOn = saunaLighting !== 'none';
    return {
      cableType: 'Zonstyrd belysningskabel enligt SEK, ingen kraftmatning till vedkamin.',
      cableTypeKey: 'domain_cable_typeWoodBell',
      fuseLabel: 'Separat grupp för belysning/styrning enligt elektriker',
      fuseLabelKey: 'domain_cable_fuseLightingGroup',
      rcdLabel: 'Jordfelsbrytare 30 mA: OBLIGATORISK för bastuns elinstallation.',
      rcdLabelKey: 'domain_cable_rcd',
      sihfLengthM: lightingOn ? sihfLengthM : 0,
      totalCableLengthM: lightingOn ? totalCableLengthM : 0,
      ...(isLegacyRouting ? {} : { routing }),
      warnings: lightingOn ? ['Vedeldad kamin kräver inte kraftmatning, men belysning i varm zon ska alltid gå med SiHF-kabel.'] : [],
      warningKeys: lightingOn ? ['domain_cable_warningWood'] : [],
      materials: lightingOn ? [
        { material: 'Silikonkabel', dimension: 'SiHF 3×1.5 mm²', quantity: `${sihfLengthM} lpm`, code: 'sihf-cable', category: 'electrical', phase: 'systems', priceRuleId: 'prices.silikonkabel', materialKey: 'electrical.silicone-cable' },
        { material: 'Kopplingsmaterial', dimension: 'Varmzonsel', quantity: '1 paket', code: 'lighting-accessories', category: 'electrical', phase: 'systems', priceRuleId: 'prices.kopplingsmaterial', materialKey: 'electrical.accessories' },
        { material: 'Bastuarmatur', dimension: 'IP-klassad / värmetålig', quantity: '1 st', code: 'lighting-fixture', category: 'electrical', phase: 'systems', priceRuleId: 'prices.bastuarmatur', materialKey: 'electrical.fixture' },
      ] : [],
    };
  }

  const fuseLabelKey = getFuseLabelKey(recommendedKw);
  const hasLargeUnit = recommendedKw > 7.5;

  const result: CableSpecResult = {
    cableType: getCableType(recommendedKw),
    cableTypeKey: 'domain_cable_typeFeed',
    cableTypeArgs: { size: getCableType(recommendedKw).replace(/ EKLK$/, '') },
    fuseLabel: getFuseLabel(recommendedKw),
    fuseLabelKey,
    rcdLabel: 'Jordfelsbrytare 30 mA: OBLIGATORISK',
    rcdLabelKey: 'domain_cable_rcdObligatory',
    sihfLengthM,
    totalCableLengthM,
    ...(isLegacyRouting ? {} : { routing }),
    warnings: hasLargeUnit
      ? ['Större aggregat kräver normalt trefas och bör verifieras mot fastighetens huvudsäkring.']
      : [],
    warningKeys: hasLargeUnit ? ['domain_cable_warningLargeUnit'] : [],
    materials: [
      { material: 'Standard Kabel', dimension: getCableType(recommendedKw), quantity: `${Math.max(2, Math.round(routing.feedCableLengthM))} lpm`, code: 'feed-cable', category: 'electrical', phase: 'systems', priceRuleId: 'prices.standardKabel', materialKey: 'electrical.standard-cable' },
      { material: 'Silikonkabel', dimension: getSihfCableType(recommendedKw), quantity: `${sihfLengthM} lpm`, code: 'sihf-cable', category: 'electrical', phase: 'systems', priceRuleId: 'prices.silikonkabel', materialKey: 'electrical.silicone-cable' },
      { material: 'Kopplingsmaterial', dimension: 'Klammer, dosor och skyddsrör', quantity: '1 paket', code: 'electrical-accessories', category: 'electrical', phase: 'systems', priceRuleId: 'prices.kopplingsmaterial', materialKey: 'electrical.accessories' },
      { material: 'Bastuarmatur', dimension: 'IP-klassad / värmetålig', quantity: '1 st', code: 'lighting-fixture', category: 'electrical', phase: 'systems', priceRuleId: 'prices.bastuarmatur', materialKey: 'electrical.fixture' },
    ],
  };
  if (saunaLighting === 'none') result.materials = result.materials.filter(row => row.code !== 'lighting-fixture');
  return result;
}
